import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Shield, LogOut, RefreshCw, UserPlus, Pencil, Check, ArrowUp, ArrowDown, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { HIPAABanner } from "@/components/HIPAACompliance";
import StatusBadge from "@/components/admin/StatusBadge";
import TrainingHelpButton from "@/components/TrainingHelpButton";
import UnreadBadge from "@/components/UnreadBadge";
import useTrainingUnread from "@/hooks/useTrainingUnread";
import PinTabButton, { useDefaultLandingTab } from "@/components/PinTabButton";
import ThemeToggle from "@/components/ThemeToggle";
import { useAdminNavOrder } from "@/hooks/useAdminNavOrder";
import useChatBotPendingCount from "@/hooks/useChatBotPendingCount";

const COLLAPSED_KEY_PREFIX = "glpflo-admin-sidebar-collapsed:";
function readCollapsed(uid) {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem(`${COLLAPSED_KEY_PREFIX}${uid || "anon"}`) === "1"; }
  catch { return false; }
}
function writeCollapsed(uid, val) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(`${COLLAPSED_KEY_PREFIX}${uid || "anon"}`, val ? "1" : "0"); }
  catch { /* ignore */ }
}

import adminNavItems, { findMainForLeaf } from "@/components/admin/adminNavItems";
import AdminTabRouter from "@/components/admin/AdminTabRouter";
import EditUserModal from "@/components/admin/EditUserModal";
import PatientDetailModal from "@/components/PatientDetailModal";
import CreateEmployeeModal from "@/components/admin/CreateEmployeeModal";

const EMP_FORM_DEFAULTS = {
  name: "",
  role: "prescriber",
  prescriber_type: "physician",
  supervising_physician_name: "",
  personal_email: "",
  password: "",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [erxRecords, setErxRecords] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const { unreadCount: trainingUnread, markSeen: markTrainingSeen } = useTrainingUnread(true);

  // Sidebar-badge polling: severe symptoms, unlock requests, pharmacy inquiries.
  const [unlockReqCount, setUnlockReqCount] = useState(0);
  const [severeSymptomCount, setSevereSymptomCount] = useState(0);
  const [pharmacyNewCount, setPharmacyNewCount] = useState(0);
  const [notifPerm, setNotifPerm] = useState(
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "default"
  );
  const lastNotifiedRef = useRef({ severe: -1, unlock: -1 });
  const wellnessBadgeCount = unlockReqCount + severeSymptomCount;

  useEffect(() => {
    let alive = true;
    const fetchCounts = () => {
      API.get("/wellness/admin/unlock-requests/count")
        .then(({ data }) => { if (alive) setUnlockReqCount(data.pending_count || 0); })
        .catch(() => {});
      API.get("/pharmacy-affiliate/inquiries?status=new&limit=1")
        .then(({ data }) => { if (alive) setPharmacyNewCount(data?.counts?.new || 0); })
        .catch(() => {});
      API.get("/wellness/admin/severe-symptoms/count")
        .then(({ data }) => {
          if (!alive) return;
          const next = data.pending_count || 0;
          const prev = lastNotifiedRef.current.severe;
          if (
            prev !== -1 &&
            next > prev &&
            typeof window !== "undefined" &&
            "Notification" in window &&
            Notification.permission === "granted"
          ) {
            try {
              const n = new Notification("⚠️ Severe symptom logged", {
                body: `A patient just reported a severe symptom. ${next} pending review.`,
                icon: "/icon-192.png",
                tag: "glpflo-severe-symptom",
                requireInteraction: false,
              });
              n.onclick = () => {
                try { window.focus(); } catch (_) { /* noop */ }
                setActiveTab("severe-symptoms");
                n.close();
              };
            } catch (_) { /* fail-silent */ }
          }
          lastNotifiedRef.current.severe = next;
          setSevereSymptomCount(next);
        })
        .catch(() => {});
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const requestNotifPermission = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    try {
      const result = await Notification.requestPermission();
      setNotifPerm(result);
      if (result === "granted") {
        toast.success("Desktop alerts enabled — you'll be notified the moment a severe symptom lands.");
      } else if (result === "denied") {
        toast.error("Permission blocked. Update via your browser's site settings to re-enable.");
      }
    } catch (_) { /* noop */ }
  };

  const defaultLandingTab = useDefaultLandingTab();
  const appliedDefaultOnce = useRef(false);
  useEffect(() => {
    if (appliedDefaultOnce.current) return;
    if (defaultLandingTab && defaultLandingTab !== activeTab) {
      setActiveTab(defaultLandingTab);
    }
    if (defaultLandingTab !== undefined && defaultLandingTab !== null) {
      appliedDefaultOnce.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLandingTab]);

  // Inline help-deeplink: <HelpLink /> buttons sprinkled across admin
  // surfaces dispatch this event; we switch to the training tab and let
  // the manual handle the URL-hash-driven scroll/expand.
  useEffect(() => {
    const onOpenTraining = () => setActiveTab("training");
    window.addEventListener("glpflo:open-training", onOpenTraining);
    return () => window.removeEventListener("glpflo:open-training", onOpenTraining);
  }, []);

  const [editUser, setEditUser] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [editProfile, setEditProfile] = useState(null);
  const [editProfileLoading, setEditProfileLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [empForm, setEmpForm] = useState(EMP_FORM_DEFAULTS);
  const [createdEmp, setCreatedEmp] = useState(null);

  useEffect(() => { loadAll(); }, []);

  // When the admin opens the Training Manual tab, mark all announcements read.
  useEffect(() => {
    if (activeTab === "training") markTrainingSeen();
  }, [activeTab, markTrainingSeen]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, ordersRes, erxRes] = await Promise.all([
        API.get("/admin/stats"),
        API.get("/admin/users"),
        API.get("/orders"),
        API.get("/erx-records"),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setOrders(ordersRes.data);
      setErxRecords(erxRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const toggleActive = async (u) => {
    try {
      await API.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
      toast.success(`User ${u.is_active ? "deactivated" : "activated"}`);
      loadAll();
    } catch { toast.error("Failed to update user"); }
  };

  const openEditUser = async (u) => {
    setEditUser(u); setEditRole(u.role);
    setEditProfileLoading(true);
    try {
      const { data } = await API.get(`/admin/users/${u.id}`);
      setEditProfile(data);
    } catch {
      setEditProfile(u);
    } finally {
      setEditProfileLoading(false);
    }
  };

  const closeEditUser = () => { setEditUser(null); setEditProfile(null); };
  // Patient deep-view (Wellness / ID / Notes / Orders / Calls / Texts / Receipts).
  // Admins & super-admins can open any patient profile read-mostly via this
  // modal. Editing prescriptions stays out-of-band — there is no rx editor
  // here; that's handled in the Prescriber Dashboard where prescribe-cap
  // gating applies.
  const [viewingPatientId, setViewingPatientId] = useState(null);
  const openPatientDetail = (u) => setViewingPatientId(u.id);
  const closePatientDetail = () => setViewingPatientId(null);
  const closeCreateModal = () => { setShowCreateModal(false); setCreatedEmp(null); };
  const openCreateModal = () => { setShowCreateModal(true); setCreatedEmp(null); setEmpForm(EMP_FORM_DEFAULTS); };

  const handleLogout = async () => { await logout(); navigate("/cliniclogin"); };
  const staffUsers = useMemo(() => users.filter(u => u.role !== "patient"), [users]);

  const activeMain = findMainForLeaf(activeTab);
  const activeSubs = activeMain?.subs || null;
  const goToMain = (item) => setActiveTab(item.subs ? item.subs[0].key : item.key);

  // Per-user persistent sidebar ordering + Edit Mode toggle.
  // Filter out items flagged `hidden:true` in the nav config so we can
  // dark-launch / retire entire sections (e.g. Live Chat) without
  // deleting the routes — they stay reachable by direct URL.
  const visibleNav = useMemo(() => adminNavItems.filter((it) => !it.hidden), []);
  const {
    orderedMain, orderedSubsFor, setMainOrder, setSubOrder, resetAll, isCustomized,
  } = useAdminNavOrder(user?.id, visibleNav);
  const orderedActiveSubs = activeSubs ? orderedSubsFor(activeMain.key) : null;
  // Red-badge count for the Chat Bot sub-tab (unanswered questions).
  const chatBotPending = useChatBotPendingCount(user?.role === "admin" || user?.role === "super_admin");
  const [navEdit, setNavEdit] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readCollapsed(user?.id));
  // Sync collapsed pref when user changes (e.g. account switch in same tab).
  useEffect(() => { setCollapsed(readCollapsed(user?.id)); }, [user?.id]);
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(user?.id, next);
    if (next) setNavEdit(false); // can't edit while collapsed
  };
  const moveMain = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= orderedMain.length) return;
    const keys = orderedMain.map((m) => m.key);
    [keys[idx], keys[j]] = [keys[j], keys[idx]];
    setMainOrder(keys);
  };
  const moveSub = (idx, dir) => {
    if (!orderedActiveSubs) return;
    const j = idx + dir;
    if (j < 0 || j >= orderedActiveSubs.length) return;
    const keys = orderedActiveSubs.map((s) => s.key);
    [keys[idx], keys[j]] = [keys[j], keys[idx]];
    setSubOrder(activeMain.key, keys);
  };

  const routerCtx = {
    stats, orders, staffUsers, users, erxRecords,
    roleFilter, setRoleFilter,
    openEditUser, toggleActive, loadAll, openPatientDetail,
    notifPerm, requestNotifPermission,
    setActiveTab,
  };

  const sidebarBadges = (key) => {
    if (key === "training") return <UnreadBadge count={trainingUnread} />;
    if (key === "wellness-group") return <UnreadBadge count={wellnessBadgeCount} />;
    if (key === "pharmacy-group") return <UnreadBadge count={pharmacyNewCount} />;
    if (key === "pharmacy-inquiries") return <UnreadBadge count={pharmacyNewCount} />;
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar — locked (sticky). Shown from md: so iPad portrait
            gets the full nav (previously hidden below 1024px). The
            collapse toggle lets a tablet user shrink it to icons-only
            to reclaim horizontal space. */}
        <aside
          className={`hidden md:flex flex-col bg-white dark:bg-slate-900 border-r border-slate-300 dark:border-slate-800 sticky top-0 self-start h-screen overflow-y-auto transition-[width] duration-200 ease-in-out ${
            collapsed ? "w-16 p-3 items-center" : "w-64 p-6"
          }`}
          data-testid="admin-sidebar"
          data-collapsed={collapsed ? "true" : "false"}
        >
          {/* Top: brand + collapse toggle */}
          <div className={`flex items-center mb-6 gap-2 w-full ${collapsed ? "justify-center" : "justify-between"}`}>
            <div className={`flex items-center gap-2 min-w-0 ${collapsed ? "justify-center" : ""}`}>
              <div className="w-9 h-9 rounded-xl bg-purple-600 flex items-center justify-center flex-shrink-0"><Shield className="w-5 h-5 text-white" /></div>
              {!collapsed && (
                <div className="min-w-0">
                  <span className="font-bold text-lg text-slate-900 dark:text-white block truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>GLPFlo</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">Super Admin</span>
                </div>
              )}
            </div>
            {!collapsed && (
              <div className="flex-shrink-0 mt-1"><StatusBadge variant="sidebar" onClick={() => setActiveTab("health")} /></div>
            )}
          </div>

          {/* Collapse toggle button */}
          <button
            type="button"
            onClick={toggleCollapsed}
            className={`mb-3 flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors ${
              collapsed ? "self-center w-9 h-9 justify-center" : "self-start"
            }`}
            data-testid="admin-sidebar-collapse-toggle"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <><ChevronLeft className="w-3.5 h-3.5" /> Collapse</>}
          </button>

          {/* Edit-mode toolbar (hidden when collapsed) */}
          {!collapsed && (
            <div className="mb-3 flex items-center justify-between gap-2 px-1">
              <button
                type="button"
                onClick={() => setNavEdit((v) => !v)}
                className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full transition-colors ${
                  navEdit
                    ? "bg-purple-600 text-white"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                }`}
                data-testid="admin-sidebar-edit-toggle"
                aria-pressed={navEdit}
              >
                {navEdit ? <Check className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                {navEdit ? "Done" : "Rearrange"}
              </button>
              {navEdit && isCustomized && (
                <button
                  type="button"
                  onClick={() => { resetAll(); toast.success("Sidebar order reset"); }}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600"
                  data-testid="admin-sidebar-reset"
                  title="Reset to default order"
                >
                  <RotateCcw className="w-3 h-3" /> Reset
                </button>
              )}
            </div>
          )}

          <nav className={`flex-1 ${collapsed ? "space-y-1.5 w-full" : "space-y-1"}`}>
            {orderedMain.map((item, idx) => {
              const isActive = activeMain?.key === item.key;
              if (collapsed) {
                return (
                  <button
                    key={item.key}
                    onClick={() => goToMain(item)}
                    data-testid={`admin-nav-${item.key}`}
                    title={item.label}
                    aria-label={item.label}
                    className={`w-full flex items-center justify-center rounded-lg p-2.5 transition-colors ${
                      isActive
                        ? "text-purple-700 bg-purple-100 dark:text-white dark:bg-purple-600/30"
                        : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                  </button>
                );
              }
              return (
                <div key={item.key} className="flex items-center gap-1">
                  {navEdit && (
                    <div className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => moveMain(idx, -1)}
                        disabled={idx === 0}
                        className="p-0.5 text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-30"
                        aria-label={`Move ${item.label} up`}
                        data-testid={`admin-nav-${item.key}-up`}
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveMain(idx, 1)}
                        disabled={idx === orderedMain.length - 1}
                        className="p-0.5 text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-30"
                        aria-label={`Move ${item.label} down`}
                        data-testid={`admin-nav-${item.key}-down`}
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <button onClick={() => !navEdit && goToMain(item)}
                    disabled={navEdit}
                    data-testid={`admin-nav-${item.key}`}
                    className={`group flex-1 flex items-center gap-3 text-sm rounded-lg px-3 py-2.5 transition-colors ${
                      navEdit
                        ? "text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 border border-dashed border-slate-300 dark:border-slate-700 cursor-default"
                        : isActive
                          ? "text-purple-700 bg-purple-100 dark:text-white dark:bg-purple-600/30"
                          : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}>
                    <item.icon className="w-4 h-4" /> <span className="flex-1 text-left">{item.label}</span>
                    {sidebarBadges(item.key)}
                    {!navEdit && <PinTabButton activeKey={item.key} />}
                  </button>
                </div>
              );
            })}
          </nav>
          <div className={`mt-auto pt-6 border-t border-slate-300 dark:border-slate-800 w-full ${collapsed ? "flex flex-col items-center gap-2" : ""}`}>
            {!collapsed && (
              <>
                <p className="text-sm text-slate-900 dark:text-white font-medium">{user?.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{user?.employee_id || user?.email}</p>
                <div className="flex items-center gap-2 mb-2">
                  <ThemeToggle variant="sidebar" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">Theme</span>
                </div>
                <Button variant="ghost" size="sm" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white w-full justify-start" onClick={handleLogout} data-testid="admin-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </Button>
              </>
            )}
            {collapsed && (
              <>
                <ThemeToggle variant="sidebar" />
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                  title="Sign out"
                  aria-label="Sign out"
                  data-testid="admin-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </aside>

        <main className="flex-1 min-w-0 p-6 lg:p-8">
          <div className="md:hidden flex items-center justify-between mb-6">
            <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-purple-600" /><span className="font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Admin</span></div>
            <div className="flex items-center gap-2">
              <StatusBadge variant="light" onClick={() => setActiveTab("health")} />
              <ThemeToggle variant="light" />
              <Button variant="ghost" size="sm" onClick={handleLogout}><LogOut className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="md:hidden flex gap-2 mb-6 overflow-x-auto pb-2">
            {orderedMain.map((item) => {
              const isActive = activeMain?.key === item.key;
              return (
                <button key={item.key} onClick={() => goToMain(item)}
                  className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-full whitespace-nowrap ${isActive ? "bg-purple-600 text-white" : "bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}>
                  <item.icon className="w-3.5 h-3.5" /> {item.label}
                  {sidebarBadges(item.key) && <span className="ml-1">{sidebarBadges(item.key)}</span>}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {activeMain?.label || "Overview"}
            </h1>
            <div className="flex gap-2">
              {activeTab === "users" && (
                <Button size="sm" className="rounded-lg bg-purple-600 hover:bg-purple-700 text-white" onClick={openCreateModal} data-testid="create-employee-btn">
                  <UserPlus className="w-4 h-4 mr-1" /> Create Staff
                </Button>
              )}
              <TrainingHelpButton onOpenFullManual={() => setActiveTab("training")} variant="light" />
              <Button variant="outline" size="sm" className="rounded-lg" onClick={loadAll} data-testid="admin-refresh-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
            </div>
          </div>

          {loading && <div className="text-center py-12 text-slate-400 dark:text-slate-500">Loading...</div>}
          {!loading && (
            <div>
              <HIPAABanner />

              {/* Sub-tabs strip (only for main tabs that group multiple sections) */}
              {activeSubs && (
                <div className="mb-6 flex items-center gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-300 dark:border-slate-800 overflow-x-auto" data-testid="admin-subtabs">
                  {(orderedActiveSubs || activeSubs).map((sub, idx) => {
                    const isActive = activeTab === sub.key;
                    return (
                      <div key={sub.key} className="flex items-center">
                        {navEdit && (
                          <div className="flex flex-col mr-0.5">
                            <button
                              type="button"
                              onClick={() => moveSub(idx, -1)}
                              disabled={idx === 0}
                              className="p-0.5 text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-30"
                              aria-label={`Move ${sub.label} left`}
                              data-testid={`admin-subtab-${sub.key}-up`}
                            >
                              <ArrowUp className="w-3 h-3 -rotate-90" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveSub(idx, 1)}
                              disabled={idx === (orderedActiveSubs || activeSubs).length - 1}
                              className="p-0.5 text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-30"
                              aria-label={`Move ${sub.label} right`}
                              data-testid={`admin-subtab-${sub.key}-down`}
                            >
                              <ArrowDown className="w-3 h-3 -rotate-90" />
                            </button>
                          </div>
                        )}
                        <button
                          onClick={() => !navEdit && setActiveTab(sub.key)}
                          disabled={navEdit}
                          data-testid={`admin-subtab-${sub.key}`}
                          className={`flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-lg whitespace-nowrap transition-colors ${
                            navEdit
                              ? "text-slate-700 dark:text-slate-300 bg-white/60 dark:bg-slate-800/40 border border-dashed border-slate-300 dark:border-slate-700 cursor-default"
                              : isActive
                                ? "bg-white dark:bg-slate-800 text-purple-700 dark:text-white shadow-sm"
                                : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                          }`}
                        >
                          <sub.icon className="w-3.5 h-3.5" /> {sub.label}
                          {sub.key === "chat-bot" && chatBotPending > 0 && (
                            <span
                              className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-rose-500 text-white"
                              data-testid="chat-bot-pending-badge"
                              title={`${chatBotPending} unanswered question${chatBotPending === 1 ? "" : "s"} awaiting review`}
                            >
                              {chatBotPending > 99 ? "99+" : chatBotPending}
                            </span>
                          )}
                        </button>
                      </div>
                    );
                  })}
                  {navEdit && (
                    <button
                      type="button"
                      onClick={() => { setSubOrder(activeMain.key, activeMain.subs.map((s) => s.key)); toast.success("Sub-tab order reset"); }}
                      className="ml-2 flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600"
                      data-testid="admin-subtabs-reset"
                      title="Reset this group's sub-tab order"
                    >
                      <RotateCcw className="w-3 h-3" /> Reset
                    </button>
                  )}
                </div>
              )}

              <AdminTabRouter activeTab={activeTab} ctx={routerCtx} />
            </div>
          )}
        </main>
      </div>

      <EditUserModal
        open={!!editUser}
        editUser={editUser}
        editRole={editRole}
        setEditRole={setEditRole}
        editProfile={editProfile}
        setEditProfile={setEditProfile}
        loading={editProfileLoading}
        onClose={closeEditUser}
        onSaved={() => { closeEditUser(); loadAll(); }}
      />

      <CreateEmployeeModal
        open={showCreateModal}
        empForm={empForm}
        setEmpForm={setEmpForm}
        createdEmp={createdEmp}
        setCreatedEmp={setCreatedEmp}
        onClose={closeCreateModal}
        onCreated={loadAll}
      />

      {/* Patient deep-view — opens on Team-row click. Read-mostly: shows
          demographics, wellness, notes, orders, ID-verification, calls,
          texts, receipts. Prescription editing intentionally absent —
          that lives in the Prescriber Dashboard with capability gating. */}
      <PatientDetailModal
        open={!!viewingPatientId}
        onClose={closePatientDetail}
        patientId={viewingPatientId}
      />
    </div>
  );
}
